import styled from "@emotion/styled";
import ArrowOnTargetIcon from "img/home/arrow-on-target.svg?react";
import { Badge } from "./element/Badge";
import { Title as TitleOriginal } from "./element/Title";
import { Text } from "components/general/Text";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Theme } from "styles/theme";
import { css } from "@emotion/react";
import { GlobalStore } from "store/global/GlobalStore";
import { clamp, throttle, times } from "lodash-es";
import { AnimateChangeInHeight } from "components/general/AnimateChangeInHeight";
import { MOBILE_WIDTH, TABLET_WIDTH, useIsMobile } from "hooks/useDeviceType";
import { useElementSize } from "usehooks-ts";

export const FeatureProtection = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [contentRef, contentRefSize] = useElementSize();
  const isMobile = useIsMobile();
  const [sectionInFocus, setSectionInFocus] = useState(0);
  const theme = GlobalStore.useState(s => s.theme);
  const scrollPxPerSection = Math.floor(window.innerHeight / 3);

  const handleScroll = useCallback(() => {
    const top = ref.current?.getBoundingClientRect().top;
    if (top == null) return;
    const topCutoff = window.innerHeight / 5;
    if (top > topCutoff) return;
    const nextSectionInFocus = clamp(
      Math.floor((topCutoff - top) / scrollPxPerSection),
      0,
      CONTENT_TEXT.length - 1
    );
    throttle(() => setSectionInFocus(nextSectionInFocus), 300)();
  }, [scrollPxPerSection]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll]);

  const borderCircleSize = useMemo(() => {
    const { width, height } = contentRefSize;
    const halfPerimeter = Math.ceil(width * 1.2) + Math.ceil(height * 1.3);
    const MAX_DESKTOP_SIZE = 120;
    const MAX_MOBILE_SIZE = 50;
    const MIN_DESKTOP_SIZE = 90;
    const MIN_MOBILE_SIZE = 68;
    const maxSize = isMobile ? MAX_MOBILE_SIZE : MAX_DESKTOP_SIZE;
    const minSize = isMobile ? MIN_MOBILE_SIZE : MIN_DESKTOP_SIZE;

    let currSize = maxSize;
    let valueOfMinRemainder = [currSize, Infinity] as const;

    while (currSize >= minSize) {
      const remainder = halfPerimeter % currSize;
      if (remainder < valueOfMinRemainder[1]) {
        valueOfMinRemainder = [currSize, remainder];
      }
      currSize -= 1;
    }

    return valueOfMinRemainder[0] ?? maxSize;
  }, [contentRefSize, isMobile]);

  const CircleBorder = ({ position }: { position: "top" | "bottom" | "left" | "right" }) => {
    const [borderRef, borderRefSize] = useElementSize();
    const theme = GlobalStore.useState(s => s.theme);
    const isHorizontal = position === "top" || position === "bottom";
    const isVertical = position === "left" || position === "right";

    const containerLength = Math.ceil(isHorizontal ? contentRefSize.width : contentRefSize.height);

    if (containerLength === 0) return;

    let count = Math.ceil(containerLength / borderCircleSize);
    if (isHorizontal) {
      count -= 1;
    }
    const offsetDirection = isHorizontal ? "left" : "top";

    return (
      <div
        ref={borderRef}
        style={{
          display: "flex",
          position: "absolute",
          [position]: -Math.ceil(borderCircleSize / 2),
          ...(isVertical
            ? { top: Math.ceil((-(borderRefSize.height - containerLength) + 2 * count) / 2) }
            : {}),
          ...(isHorizontal
            ? { left: Math.ceil((-(borderRefSize.width - containerLength) + 2 * count) / 2) }
            : {}),
          ...(!isHorizontal ? { flexDirection: "column" } : {}),
        }}
      >
        {times(count, i => (
          <div
            key={i}
            style={{
              backgroundColor: theme["Colour ramps"].Blue[700],
              width: borderCircleSize,
              height: borderCircleSize,
              borderRadius: "50%",
              position: "relative",
              ...(i !== 0 ? { [offsetDirection]: -2 * i } : {}),
            }}
          />
        ))}
      </div>
    );
  };

  return (
    <Container ref={ref}>
      {/* <CloudBg /> */}
      <Content>
        <StickyContent ref={contentRef}>
          <CircleBorder position="top" />
          <CircleBorder position="left" />
          <CircleBorder position="right" />
          <CircleBorder position="bottom" />
          <div
            style={{
              position: "relative",
              zIndex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 32,
              width: "100%",
            }}
          >
            <Badge icon={ArrowOnTargetIcon} text="Protection" />
            {CONTENT_TEXT.map((Section, i) => (
              <Section key={i} isFocused={i === sectionInFocus} theme={theme} />
            ))}
          </div>
        </StickyContent>
      </Content>
    </Container>
  );
};

// Sub-components

const Container = styled.section`
  position: relative;
  margin: 0 10%;
  margin-bottom: 20dvh;
  padding: 0 83px;
  width: 100%;

  @media (max-width: ${TABLET_WIDTH}px) {
    padding: 0;
  }
`;

const Content = styled.div`
  position: relative;
  height: 150dvh;
  text-align: center;
  width: 100%;
  display: flex;
  justify-content: center;
`;

const StickyContent = styled.div`
  position: sticky;
  top: 18dvh;
  width: 80%;
  height: 40%;
  // max-height: 650px;
  transform: translateY(10%);
  border-radius: 10%;

  vertical-align: baseline;
  box-sizing: border-box;
  background: ${props => props.theme["Colour ramps"].Blue[700]};
  padding: 66px 10%;
  display: flex;
  justify-content: cetner;
  align-items: center;

  @media (max-width: ${TABLET_WIDTH}px) {
    top: 18dvh;
  }

  @media (max-width: ${MOBILE_WIDTH}px) {
    top: 26dvh;
  }

  @media (max-height: 670px) {
    top: 8dvh;
  }

  @media (max-width: 300px) {
    top: 15dvh;
  }
`;

const Title = styled(TitleOriginal)`
  margin-bottom: 12px;
`;

// Helpers

const CONTENT_TEXT = [
  ({ isFocused, theme }: { isFocused: boolean; theme: Theme }) => (
    <AnimateChangeInHeight>
      <Title
        css={css`
          color: ${isFocused ? "inherit" : theme["Colour ramps"].Blue[300]};
          @media (max-width: 1056px) {
            font-size: 70px;
          }
          @media (max-width: 680px) {
            font-size: 50px;
          }
        `}
      >
        No slips.
      </Title>
      {isFocused && (
        <div>
          <Text
            textStyle="Title"
            size="Large"
            shouldScale={true}
            textCss={css`
              max-width: 600px;
            `}
          >
            About to trade and the price drops?
          </Text>
          <Text
            textStyle="Title"
            size="Large"
            shouldScale={true}
            textCss={css`
              max-width: 600px;
            `}
          >
            Bebop has 0% slippage. It doesn’t happen here.
          </Text>
        </div>
      )}
    </AnimateChangeInHeight>
  ),

  ({ isFocused, theme }: { isFocused: boolean; theme: Theme }) => (
    <AnimateChangeInHeight>
      <Title
        css={css`
          color: ${isFocused ? "inherit" : theme["Colour ramps"].Blue[300]};
          @media (max-width: 1056px) {
            font-size: 70px;
          }
          @media (max-width: 680px) {
            font-size: 50px;
          }
        `}
      >
        No price attacks.
      </Title>
      {isFocused && (
        <Text
          textStyle="Title"
          size="Large"
          shouldScale={true}
          textCss={css`
            max-width: 600px;
          `}
        >
          All your trades are always protected from arbitrage bots.
        </Text>
      )}
    </AnimateChangeInHeight>
  ),
  ({ isFocused, theme }: { isFocused: boolean; theme: Theme }) => (
    <AnimateChangeInHeight
      contentStyle={{ display: "flex", flexDirection: "column", alignItems: "center" }}
    >
      <Title
        css={css`
          color: ${isFocused ? "inherit" : theme["Colour ramps"].Blue[300]};
          @media (max-width: 1056px) {
            font-size: 70px;
          }
          @media (max-width: 680px) {
            font-size: 50px;
          }
        `}
      >
        No failed fees.
      </Title>
      {isFocused && (
        <Text
          textStyle="Title"
          size="Large"
          shouldScale={true}
          textCss={css`
            max-width: 600px;
          `}
        >
          Imagine your trade failing and you having to pay a hefty network fee for it still? Not
          with us.
        </Text>
      )}
    </AnimateChangeInHeight>
  ),
];
